import React from 'react';
import { Upload, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import RichTextEditor from '../../../components/RichTextEditor';

const HolderForm = ({
  editing,
  formData,
  setFormData,
  uploading,
  onImageUpload,
  onRemoveImage,
  onRemoveCertificate,
  onSubmit,
  onCancel,
}) => (
  <div className="bg-white rounded-lg shadow-lg p-6 mb-8" data-testid="holder-form">
    <h2 className="text-xl font-bold mb-4">{editing ? 'Edit Record Holder' : 'Add New Record Holder'}</h2>
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
        <input
          type="text"
          data-testid="holder-name-input"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
        <textarea
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          rows="2"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Achievement</label>
        <RichTextEditor
          testId="holder-achievement-input"
          value={formData.achievement}
          onChange={(achievement) => setFormData({ ...formData, achievement })}
          minHeight="11rem"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
          <input
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: Number(e.target.value) })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
        <label className="flex items-center gap-3 mt-7">
          <input
            type="checkbox"
            checked={formData.published}
            onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
            className="h-4 w-4"
          />
          <span className="text-sm font-medium text-gray-700">Published on public site</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Record Images (Multiple)</label>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-3">
            {formData.images.map((img, index) => (
              <div key={img} className="relative w-32 h-32 border rounded-lg overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  data-testid={`remove-record-image-${index}`}
                  onClick={() => onRemoveImage(index)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-red-500">
              <div className="text-center">
                <Upload className="w-8 h-8 mx-auto text-gray-400" />
                <p className="text-xs text-gray-500 mt-1">Upload</p>
              </div>
              <input
                type="file"
                data-testid="upload-record-images"
                multiple
                accept="image/*"
                onChange={(e) => onImageUpload(e, 'record')}
                className="hidden"
              />
            </label>
          </div>
          {uploading && <p className="text-sm text-blue-600">Uploading...</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Image</label>
        {formData.certificate_image ? (
          <div className="relative w-48 h-64 border rounded-lg overflow-hidden">
            <img
              src={formData.certificate_image}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              data-testid="remove-certificate"
              onClick={onRemoveCertificate}
              className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label className="w-48 h-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-red-500">
            <div className="text-center">
              <Upload className="w-8 h-8 mx-auto text-gray-400" />
              <p className="text-sm text-gray-500 mt-2">Upload Certificate</p>
            </div>
            <input
              type="file"
              data-testid="upload-certificate"
              accept="image/*"
              onChange={(e) => onImageUpload(e, 'certificate')}
              className="hidden"
            />
          </label>
        )}
      </div>

      <div className="flex gap-3">
        <Button type="submit" data-testid="submit-holder-btn" className="bg-red-700 hover:bg-red-800">
          {editing ? 'Update Holder' : 'Add Holder'}
        </Button>
        <Button type="button" data-testid="cancel-holder-btn" onClick={onCancel} variant="outline">
          Cancel
        </Button>
      </div>
    </form>
  </div>
);

export default HolderForm;
